import { githubAppStore } from "./data/github-app-store";
import { observable, action, computed, reaction } from "mobx";
import * as Fuse from "fuse.js";
import { GitHubRepo } from "./data/github-app-model";
import { NewAppUIStore } from "@root/management/apps/new-app/new-app-ui-store";
import { userStore } from "@root/stores";
import { apiGateway } from "@root/lib/http";
import { API } from "./data/constants";
import { ResourceRequest } from "@root/data/lib";
import { noop, sortBy, debounce } from "lodash";
import { App } from "@root/data/shell/models";
import { IApp } from "@lib/common-interfaces";
import { logger } from "@root/lib/telemetry";
import {
  GitHubBillingPlanRequest,
  DeserializedGitHubBillingPlan,
  GitHubBillingPlanErrorType,
  DeserializedGitHubBillingPlanError,
} from "./data/github-billing-plan-request";
import { FinishingUIStore } from "./finishing-ui-store";

export class GitHubAppUIStore {
  private createAndLinkAppsRequest?: ResourceRequest<any, any>;
  public newAppUIStore = new NewAppUIStore({
    defaultValues: {
      owner: userStore.currentUser,
    },
  });

  @observable public selectedRepo: GitHubRepo | undefined | null;
  @action public setSelectedRepo = (selectedRepo: GitHubRepo | null) => {
    this.selectedRepo = selectedRepo;
  };
  public finishingUIStore: FinishingUIStore | undefined | null;
  @observable public finishing: boolean = false;
  @action public setFinishing = (finishing: boolean): void => {
    this.finishing = finishing;
  };

  @observable public query: string = "";
  private fuse!: Fuse<GitHubRepo, Fuse.FuseOptions<GitHubRepo> & { includeScore: false; includeMatches: false }>;

  @observable private githubBillingPlan!: DeserializedGitHubBillingPlan;
  @observable private githubBillingPlanError!: DeserializedGitHubBillingPlanError;
  private githubBillingPlanRequest: GitHubBillingPlanRequest;

  constructor(private installationId: string, accessTokenId: string, githubUserId: string) {
    githubAppStore.fetch({ installationId, accessTokenId, githubUserId }).onSuccess((data) => {
      this.fuse = new Fuse(data!, {
        shouldSort: false,
        threshold: 0.2,
        keys: ["name"],
        includeMatches: false,
        includeScore: false,
      });

      logger.info("Repository list fetched", { scenario: "github-integration", repo_count: data && data.length });
    });

    this.githubBillingPlanRequest = new GitHubBillingPlanRequest(
      { installationId, accessTokenId, githubUserId },
      () => this.githubBillingPlan,
      (error, data) => {
        if (error) {
          this.githubBillingPlanError = error;
          return;
        }
        if (data.name && data.displayName) {
          this.newAppUIStore.updateForm("owner", {
            name: data.name,
            email: data.email,
            display_name: data.displayName,
          });
        }
        this.githubBillingPlan = data;
      }
    );

    reaction(
      () => this.selectedRepo!,
      (selectedRepo: GitHubRepo | null) => {
        if (selectedRepo) {
          this.finishingUIStore = new FinishingUIStore(selectedRepo, installationId, accessTokenId, githubUserId);
        } else {
          this.finishingUIStore = null;
        }
      }
    );
  }

  get ownerName() {
    const firstRepo = this.data[0];
    if (firstRepo) {
      return firstRepo.ownerName;
    }
  }

  get ownerAvatarURL() {
    const firstRepo = this.data[0];
    if (firstRepo) {
      return firstRepo.ownerAvatarURL;
    }
  }

  @computed get data() {
    return sortBy(githubAppStore.data || [], ({ updatedAt }) => -updatedAt);
  }

  public getRepoById(id: string): GitHubRepo | undefined {
    // TODO: use Store instead of FetchStore for constant-time lookup
    return this.data.find((repo) => repo.id === id);
  }

  private trackSearch = debounce((resultsCount: number) => {
    logger.info("Search repository", { scenario: "github-integration", result_count: resultsCount });
  }, 1500);

  @computed get filteredData() {
    if (this.query === "") {
      return this.data;
    } else {
      const result = this.fuse.search(this.query);

      this.trackSearch(result.length);
      return result;
    }
  }

  get isLoading() {
    return githubAppStore.isPending;
  }

  @computed get isCreatingApp() {
    return (
      this.newAppUIStore.isCreating ||
      (this.createAndLinkAppsRequest && (this.createAndLinkAppsRequest.isPending || this.createAndLinkAppsRequest.isLoaded))
    );
  }

  get repositoriesErrorDialogVisible() {
    const isFailed = githubAppStore.isFailed;
    const result = !githubAppStore.isPending && (isFailed || !this.data.length);

    if (result) {
      logger.info("Cannot find repository", { scenario: "github-integration", failed: isFailed });
    }

    return result;
  }

  @computed get userMatchErrorDialogVisible() {
    return this.githubBillingPlanError && this.githubBillingPlanError.type === GitHubBillingPlanErrorType.UserMatchError;
  }

  @computed get orgPermissionErrorDialogVisible() {
    return this.githubBillingPlanError && this.githubBillingPlanError.type === GitHubBillingPlanErrorType.OrgPermissionError;
  }

  @computed get billingPlanErrorDialogVisible() {
    return this.githubBillingPlanError && this.githubBillingPlanError.type === GitHubBillingPlanErrorType.BillingPlanError;
  }

  @action public setQuery = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    this.query = value;
  };

  @action public createAppAndRedirect = (repoId: string) => {
    if (this.githubBillingPlanRequest.isPending) {
      return;
    }
    this.newAppUIStore.validate();
    if (this.newAppUIStore.isValid) {
      const appRequests = this.newAppUIStore.create();
      this.finishingUIStore!.setApps(this.newAppUIStore.appsToCreate);
      this.setFinishing(true);
      const repo = this.getRepoById(repoId);
      this.createAndLinkAppsRequest = new ResourceRequest(
        Promise.all(
          appRequests.map((request) => {
            return request.promise.then((app: App | any) => {
              const telemetryProperties = {
                scenario: "github-integration",
                app_id: app.id,
                platform: app.platform,
                os: app.os,
                owner_type: app.owner && app.owner.type,
              };
              logger.info("App created", telemetryProperties);
              return this.linkRepoToApp(app, repo!).then(() => app);
            });
          })
        ).then((apps: IApp[] | any[]) => {
          if (apps.length > 1) {
            this.finishingUIStore!.setApps(apps);
            const app = apps[0];
            const telemetryProperties = {
              scenario: "github-integration",
              apps_count: apps.length,
              platform: app.platform,
              owner_type: app.owner && app.owner.type,
            };
            logger.info("Apps created", telemetryProperties);
          }
        }),
        noop,
        noop
      );
    }
  };

  private linkRepoToApp(app: App, repo: GitHubRepo) {
    return apiGateway.post<void>(API.REPO_CONFIG, {
      params: {
        ownerName: app.owner.name,
        appName: app.name,
      },
      body: {
        repo_url: repo.cloneURL,
        repo_id: repo.id,
        installation_id: this.installationId,
      },
    });
  }
}
